import { defineStore } from 'pinia'
import { apiClient } from '@/services/apiClient'

export interface CatalogLink {
  id: number
  name: string
  url: string
  icon: string
  color: string
  display_order: number
  created_at: number
  updated_at: number
}

export interface CatalogLinkInput {
  name: string
  url: string
  icon?: string
  color?: string
  display_order?: number
}

interface State {
  links: CatalogLink[]
  isLoading: boolean
  loaded: boolean
  error: string | null
}

export const useCatalogLinksStore = defineStore('catalogLinks', {
  state: (): State => ({
    links: [],
    isLoading: false,
    loaded: false,
    error: null
  }),

  getters: {
    sorted(): CatalogLink[] {
      return [...this.links].sort((a, b) => a.display_order - b.display_order || a.id - b.id)
    }
  },

  actions: {
    async fetchLinks(force = false): Promise<void> {
      if (this.loaded && !force) return
      this.isLoading = true
      this.error = null
      try {
        const response = await apiClient.get<{ data: CatalogLink[] }>('/catalog-links')
        this.links = response.data || []
        this.loaded = true
      } catch (e) {
        this.error = e instanceof Error ? e.message : 'Erreur de chargement'
        console.error('[CatalogLinksStore] fetch failed:', this.error)
      } finally {
        this.isLoading = false
      }
    },

    async create(input: CatalogLinkInput): Promise<CatalogLink> {
      const response = await apiClient.post<{ data: CatalogLink }>('/admin/catalog-links', input)
      this.links.push(response.data)
      return response.data
    },

    async update(id: number, input: Partial<CatalogLinkInput>): Promise<CatalogLink> {
      const response = await apiClient.put<{ data: CatalogLink }>(`/admin/catalog-links/${id}`, input)
      const idx = this.links.findIndex(l => l.id === id)
      if (idx >= 0) this.links[idx] = response.data
      return response.data
    },

    async remove(id: number): Promise<void> {
      await apiClient.delete(`/admin/catalog-links/${id}`)
      this.links = this.links.filter(l => l.id !== id)
    }
  }
})

export default useCatalogLinksStore
